import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import moment from 'moment';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import classNames from 'classnames';
import { useFormik } from 'formik';
import { useDropzone } from 'react-dropzone';
import { useHistory, useParams } from 'react-router-dom';
import { Col, Container, FormFeedback, Input, Row } from 'reactstrap';

import AppInputValidation from 'components/App/InputValidation';
import AppAsyncSelectValidation from 'components/App/AppAsyncSelectValidation';
import AppInputNumberValidation from 'components/App/InputNumberValidation';
import Breadcrumb from 'components/App/Breadcrumb';
import Button from 'components/App/Button';
import Card from 'components/App/Card';
import CardImage from 'components/App/CardImage';
import Loading from 'components/App/Loading';
import Meta from 'components/App/Meta';

import voucherCampaignApi from 'api/voucherCampaignApi';
import { debounce } from 'lodash';

import { LIMIT_FILE_SIZE, themeColor } from 'constants/index';
import ENDPOINT from 'constants/endPoint';
import INITIAL_VALUE from 'constants/initialValue';
import formatUploadImages from 'utils/formatUploadImages';
import {
  convertIsoDateToString,
  currencyToNumber,
  getSanitizedInteger,
  onKeyDownIntegerOnly,
} from 'utils';
// import Flatpickr from 'react-flatpickr';
// import { Vietnamese } from 'flatpickr/dist/l10n/vn';

import { ReactComponent as AddPictureIcon } from 'assets/images/add-picture.svg';
import { ReactComponent as GiftIcon } from 'assets/images/Gift-SVG.svg';
// import { ReactComponent as ChevronDown } from 'assets/images/Chervon.svg';
import { ReactComponent as PlusIcon } from 'assets/images/plus.svg';

import giftCreateValidationSchema, { GIFT_NAME_MAX_LENGTH } from 'pages/ManagerGiftCreate/giftCreateValidation';
import styles from './ManagerDetail.module.scss';

const GIFT_CREATE_TOUCHED_FIELDS = {
  name: true,
  deliveryMethod: true,
  stockQuantity: true,
  perAccountRedeemLimit: true,
  redeemPoints: true,
  images: true,
  limitStartAt: true,
  limitEndAt: true,
  voucherCampaign: true,
};

const GIFT_IMAGE_THUMB_STYLE = {
  aspectRatio: 1,
  width: 180,
  height: 167,
};

const OPTION_UPLOAD_IMAGES = {
  limitValue: 5,
  fileSize: LIMIT_FILE_SIZE.LARGE,
};

function GiftImageDragPreview({ item, style }) {
  return (
    <div className={styles.DragPreview} style={style}>
      <img src={item.image} alt="preview" />
    </div>
  );
}

GiftImageDragPreview.propTypes = {
  item: PropTypes.shape({
    image: PropTypes.string.isRequired,
  }).isRequired,
  style: PropTypes.instanceOf(Object),
};

GiftImageDragPreview.defaultProps = {
  style: GIFT_IMAGE_THUMB_STYLE,
};

const PAGE_TITLE = 'Chi tiết quà tặng';

const GIFT_TYPE = {
  EXCLUSIVE: 'EXCLUSIVE',
  VOUCHER: 'VOUCHER',
  LIMITED: 'LIMITED',
};

const GIFT_TYPE_OPTIONS = [
  {
    value: GIFT_TYPE.EXCLUSIVE,
    label: 'Quà Độc Quyền',
    title: 'Quà Độc Quyền',
  },
  {
    value: GIFT_TYPE.VOUCHER,
    label: 'Quà Voucher',
    title: 'Quà Voucher',
  },
];

const GIFT_STATUS = {
  HIDDEN: 'HIDDEN',
  PUBLIC: 'PUBLIC',
};

const DELIVERY_METHOD = {
  EMAIL: 'EMAIL',
  BOOKESE_SYSTEM: 'BOOKESE_SYSTEM',
  EXPRESS: 'EXPRESS',
};

const DELIVERY_METHOD_OPTIONS = [
  {
    value: DELIVERY_METHOD.EMAIL,
    label: 'Gửi qua Email',
  },
  // {
  //   value: DELIVERY_METHOD.BOOKESE_SYSTEM,
  //   label: 'Nhận trên hệ thống Bookese',
  // },
  {
    value: DELIVERY_METHOD.EXPRESS,
    label: 'Giao hàng',
  },
];

const GIFT_DETAIL_MAX_LENGTH = 1000;

const GIFT_DETAIL_FIELDS = [
  {
    name: 'shortDescription',
    label: 'Mô tả ngắn',
    placeholder: 'Nhập các gạch đầu dòng tóm tắt quyền lợi của voucher.',
  },
  {
    name: 'howToUse',
    label: 'Cách sử dụng',
    placeholder: 'Nhập chi tiết các bước hướng dẫn sử dụng.',
  },
  {
    name: 'termsOfUse',
    label: 'Điều kiện áp dụng',
    placeholder: 'Nhập chi tiết về khu vực, thời gian và các quy định áp dụng.',
  },
];

const mapStatusFromApi = (gift) => {
  if (!gift) return GIFT_STATUS.PUBLIC;
  if (gift.status === 'DRAFT' || gift.status === 'HIDDEN') return GIFT_STATUS.HIDDEN;
  return GIFT_STATUS.PUBLIC;
};

const breadcrumbList = [
  {
    name: 'Quà tặng',
    isActive: false,
    link: ENDPOINT.MANAGER_GIFT_LIST,
  },
  {
    name: PAGE_TITLE,
    isActive: true,
    link: '',
  },
];

function ManagerDetail({
  isLoadingUploadMultipleMedia,
  actionUploadMultiMedia,
  giftDetails: gift,
  isLoadingGetGiftDetails,
  isUpdateGiftLoading,
  actionGetGiftDetails,
  actionUpdateGift,
}) {
  const history = useHistory();
  const { id } = useParams();

  const validation = useFormik({
    enableReinitialize: true,
    validationSchema: giftCreateValidationSchema,
    initialValues: {
      name: gift?.name || '',
      giftType: gift?.category === 'VOUCHER' ? GIFT_TYPE.VOUCHER : GIFT_TYPE.EXCLUSIVE,
      deliveryMethod: gift?.deliveryMethod || '',
      isLimited: gift?.isFlashSale || false,
      voucherCampaign: gift?.voucherCampaign?._id || gift?.voucherCampaign || '',
      limitStartAt: gift?.startAt || gift?.limitStartAt || '',
      limitEndAt: gift?.endAt || gift?.limitEndAt || '',
      status: mapStatusFromApi(gift),
      images: (gift?.images || []).map((img) => ({
        image: img,
        id: img,
        checked: false,
      })),
      redeemPoints: gift?.coin ?? '',
      stockQuantity: gift?.category === 'VOUCHER' ? 0 : (gift?.quantity ?? 0),
      expiryUsage: '',
      perAccountRedeemLimit: gift?.maxQuantityPerUser ?? 0,
      shortDescription: gift?.description || gift?.shortDescription || '',
      howToUse: gift?.instructionForUse || '',
      termsOfUse: gift?.conditionApply || '',
    },
    onSubmit: async (values) => {
      const { isLimited } = values;
      const getCategory = (type) => (type === GIFT_TYPE.VOUCHER ? 'VOUCHER' : 'EXCLUSIVE');
      const GIFT_STATUS_MAP = { HIDDEN: 'DRAFT', PUBLIC: 'PUBLISHED' };

      const payload = {
        name: values.name.trim(),
        slug: gift?.slug || '',
        category: getCategory(values.giftType),
        isFlashSale: Boolean(isLimited),
        status: GIFT_STATUS_MAP[values.status] || 'DRAFT',
        deliveryMethod: values.deliveryMethod || 'BOOKESE_SYSTEM',
        coin: currencyToNumber(String(values.redeemPoints || '')) || 0,
        maxQuantityPerUser: Number(values.perAccountRedeemLimit) || 0,
        images: (values.images || []).map((item) => item.image).filter(Boolean),
        shortDescription: values.shortDescription || '',
        description: values.shortDescription || '',
        instructionForUse: values.howToUse || '',
        conditionApply: values.termsOfUse || '',
        returnPolicy: '',
      };

      if (values.giftType !== GIFT_TYPE.VOUCHER) {
        payload.quantity = Number(values.stockQuantity) || 0;
      }

      const toISOString = (val) => {
        if (!val) return undefined;
        const date = val instanceof Date ? val : new Date(val);
        return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
      };

      const startAt = toISOString(values.limitStartAt);
      const endAt = toISOString(values.limitEndAt);

      if (isLimited && startAt) {
        payload.startAt = startAt;
      }
      if (isLimited && endAt) {
        payload.endAt = endAt;
      }

      actionUpdateGift({
        id,
        payload,
        callback: () => {
          history.push(ENDPOINT.MANAGER_GIFT_LIST);
        },
      });
    },
  });

  const getFieldError = React.useCallback(
    (field) => {
      if (validation.submitCount === 0 && !validation.touched[field]) {
        return '';
      }

      return validation.errors[field] || '';
    },
    [validation.errors, validation.submitCount, validation.touched],
  );

  const onSaveGift = React.useCallback(async () => {
    const errors = await validation.validateForm();
    validation.setTouched(GIFT_CREATE_TOUCHED_FIELDS);

    if (Object.keys(errors).length === 0) {
      validation.submitForm();
    }
  }, [validation]);

  const [activeImageId, setActiveImageId] = useState(null);
  const inputFileRef = React.useRef(null);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 10 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const activeDragImage = useMemo(
    () => validation.values.images.find((item) => item.id === activeImageId),
    [activeImageId, validation.values.images],
  );

  const onUploadMultiImages = useCallback(
    (imageList) => {
      const imageUploadList = formatUploadImages(
        imageList,
        OPTION_UPLOAD_IMAGES,
      );

      if (imageUploadList.length > 0) {
        actionUploadMultiMedia({
          images: imageUploadList,
          callback: (uploadedUrls) => {
            const newImages = uploadedUrls.map((image) => ({
              image,
              id: image,
              checked: false,
            }));

            validation.setFieldValue(
              'images',
              [...validation.values.images, ...newImages],
              true,
            );
            validation.setFieldTouched('images', true, false);

            if (inputFileRef.current) {
              inputFileRef.current.value = '';
            }
          },
        });
      }
    },
    [actionUploadMultiMedia, validation],
  );

  const onChangeAddImages = useCallback(
    (e) => {
      onUploadMultiImages(e.target.files);
    },
    [onUploadMultiImages],
  );

  const onDragStart = useCallback((event) => {
    setActiveImageId(event.active.id);
  }, []);

  const onDragCancel = useCallback(() => {
    setActiveImageId(null);
  }, []);

  const onDragImage = useCallback(
    (event) => {
      const { active, over } = event;

      setActiveImageId(null);

      if (!over || active.id === over.id) {
        return;
      }

      validation.setValues(({ images }) => {
        const oldIndex = images.findIndex((img) => img.id === active.id);
        const newIndex = images.findIndex((img) => img.id === over.id);

        if (oldIndex < 0 || newIndex < 0) {
          return { images };
        }

        return { images: arrayMove(images, oldIndex, newIndex) };
      });
    },
    [validation],
  );

  const { getRootProps, getInputProps, isDragAccept } = useDropzone({
    noClick: true,
    noKeyboard: true,
    accept: { 'image/*': [] },
    multiple: true,
    onDrop: (files) => onUploadMultiImages(files),
  });

  const onClickOpenFileInput = useCallback(() => {
    inputFileRef.current?.click();
  }, []);

  const onSelectImage = useCallback(
    (e) => {
      const { name } = e.target;

      validation.setFieldValue(
        'images',
        validation.values.images.map((item) =>
          (item.id === name ? { ...item, checked: !item.checked } : item),
        ),
        true,
      );
    },
    [validation],
  );

  const onSelectAllImages = useCallback(() => {
    validation.setFieldValue(
      'images',
      validation.values.images.map((item) => ({ ...item, checked: true })),
      true,
    );
  }, [validation]);

  const onClearSelectAllImages = useCallback(() => {
    validation.setFieldValue(
      'images',
      validation.values.images.map((item) => ({ ...item, checked: false })),
      true,
    );
  }, [validation]);

  const onDeleteSelectedImages = useCallback(() => {
    validation.setFieldValue(
      'images',
      validation.values.images.filter((item) => !item.checked),
      true,
    );
  }, [validation]);

  const fetchGiftDetails = useCallback(() => {
    if (!id) return;
    actionGetGiftDetails({ id });
  }, [id, actionGetGiftDetails]);

  useEffect(() => {
    fetchGiftDetails();
  }, [fetchGiftDetails]);

  const giftTypeTitle = useMemo(() => {
    const selected = GIFT_TYPE_OPTIONS.find(
      (item) => item.value === validation.values.giftType,
    );
    return selected?.title || GIFT_TYPE_OPTIONS[0].title;
  }, [validation.values.giftType]);

  const isLimitedGift = validation.values.isLimited;
  const isExclusiveGift = validation.values.giftType === GIFT_TYPE.EXCLUSIVE;
  const isVoucherGift = validation.values.giftType === GIFT_TYPE.VOUCHER;

  const onChangeGiftType = useCallback(
    (e) => {
      const nextGiftType = e.target.value;

      if (nextGiftType === GIFT_TYPE.EXCLUSIVE) {
        validation.setValues(
          {
            ...validation.values,
            giftType: nextGiftType,
            deliveryMethod: DELIVERY_METHOD.EXPRESS,
            voucherCampaign: '',
          },
          true,
        );
      } else {
        validation.setFieldValue('giftType', nextGiftType, true);
      }

      if (nextGiftType === GIFT_TYPE.VOUCHER) {
        validation.setFieldTouched('voucherCampaign', true, false);
      }
    },
    [validation],
  );

  const [voucherCampaignOptions, setVoucherCampaignOptions] = useState([]);
  const [isLoadingVoucherCampaigns, setIsLoadingVoucherCampaigns] = useState(false);
  const [selectedCampaignDetails, setSelectedCampaignDetails] = useState(null);

  const fetchVoucherCampaignDetail = useCallback((campaignId) => {
    if (!campaignId) {
      setSelectedCampaignDetails(null);
      return;
    }
    voucherCampaignApi
      .getVoucherCampaignDetail({ id: campaignId })
      .then((response) => {
        const detail = response?.payload || response?.data || response;
        setSelectedCampaignDetails(detail);
      })
      .catch(() => {
        setSelectedCampaignDetails(null);
      });
  }, []);

  useEffect(() => {
    if (validation.values.voucherCampaign) {
      fetchVoucherCampaignDetail(validation.values.voucherCampaign);
    } else {
      setSelectedCampaignDetails(null);
    }
  }, [validation.values.voucherCampaign, fetchVoucherCampaignDetail]);

  const fetchDefaultVoucherCampaigns = useCallback(() => {
    setIsLoadingVoucherCampaigns(true);
    voucherCampaignApi
      .getListVoucherCampaign({
        unlinkedToGift: true,
        limit: 10,
      })
      .then((response) => {
        const data = response.payload?.data || response.data || response || [];
        const formatted = data.map((item) => ({
          label: item.name,
          value: item._id,
        }));

        const currentCampaign = gift?.voucherCampaign;
        if (currentCampaign) {
          const currentId = currentCampaign._id || currentCampaign;
          const currentName = currentCampaign.name || gift.voucherCampaignName || 'Chiến dịch hiện tại';
          if (!formatted.some((o) => o.value === currentId)) {
            formatted.unshift({ label: currentName, value: currentId });
          }
        }

        setVoucherCampaignOptions(formatted);
      })
      .catch(() => {
        setVoucherCampaignOptions([]);
      })
      .finally(() => {
        setIsLoadingVoucherCampaigns(false);
      });
  }, [gift]);

  useEffect(() => {
    if (validation.values.giftType === GIFT_TYPE.VOUCHER) {
      fetchDefaultVoucherCampaigns();
    }
  }, [validation.values.giftType, fetchDefaultVoucherCampaigns]);

  useEffect(() => {
    if (gift?.voucherCampaign) {
      const campaignId = gift.voucherCampaign._id || gift.voucherCampaign;
      const campaignName = gift.voucherCampaign.name || gift.voucherCampaignName || 'Chiến dịch hiện tại';
      setVoucherCampaignOptions([
        {
          label: campaignName,
          value: campaignId,
        },
      ]);
    }
  }, [gift]);

  const onSearchVoucherCampaign = useMemo(
    () =>
      debounce((inputValue, callback) => {
        voucherCampaignApi
          .getListVoucherCampaign({
            q: inputValue,
            unlinkedToGift: true,
            limit: 10,
          })
          .then((response) => {
            const data = response.payload?.data || response.data || response || [];
            const formatted = data.map((item) => ({
              label: item.name,
              value: item._id,
            }));

            const currentCampaign = gift?.voucherCampaign;
            if (currentCampaign) {
              const currentId = currentCampaign._id || currentCampaign;
              const currentName = currentCampaign.name || gift.voucherCampaignName || 'Chiến dịch hiện tại';
              if (!formatted.some((o) => o.value === currentId)) {
                formatted.unshift({ label: currentName, value: currentId });
              }
            }

            setVoucherCampaignOptions(formatted);
            callback(formatted);
          })
          .catch(() => {
            callback([]);
          });
      }, 500),
    [gift],
  );

  const onSelectVoucherCampaign = useCallback(
    (e) => {
      validation.setFieldValue('voucherCampaign', e.value, true);
      validation.setFieldTouched('voucherCampaign', true, false);
      validation.setFieldValue('limitStartAt', '', true);
      validation.setFieldValue('limitEndAt', '', true);
    },
    [validation],
  );

  const onMenuCloseVoucherCampaign = useCallback(() => {
    fetchDefaultVoucherCampaigns();
  }, [fetchDefaultVoucherCampaigns]);

  const onChangeIsLimited = useCallback(
    (e) => {
      const isChecked = e.target.checked;
      validation.setFieldValue('isLimited', isChecked);

      if (!isChecked) {
        validation.setFieldValue('limitStartAt', '', true);
        validation.setFieldValue('limitEndAt', '', true);
        validation.setFieldTouched('limitStartAt', false, false);
        validation.setFieldTouched('limitEndAt', false, false);
      }
    },
    [validation],
  );

  const isDeliveryOptionDisabled = useCallback(
    (value) => isExclusiveGift && value !== DELIVERY_METHOD.EXPRESS,
    [isExclusiveGift],
  );

  const onChangeDeliveryMethod = useCallback(
    (e) => {
      validation.setFieldValue('deliveryMethod', e.target.value, true);
      validation.setFieldTouched('deliveryMethod', true, false);
    },
    [validation],
  );

  const onChangeStatus = useCallback(
    (e) => {
      validation.setFieldValue('status', e.target.value);
    },
    [validation],
  );

  const exchangedQuantity = gift?.exchangedQuantity ?? gift?.redeemedCount ?? 0;

  const onAdjustStock = useCallback(
    (delta) => () => {
      validation.setFieldValue(
        'stockQuantity',
        Math.min(
          999,
          Math.max(
            exchangedQuantity,
            (Number(validation.values.stockQuantity) || 0) + delta,
          ),
        ),
        true,
      );
      validation.setFieldTouched('stockQuantity', true, false);
    },
    [validation, exchangedQuantity],
  );

  const onAdjustPerAccountLimit = useCallback(
    (delta) => () => {
      validation.setFieldValue(
        'perAccountRedeemLimit',
        Math.min(
          999,
          Math.max(
            0,
            (Number(validation.values.perAccountRedeemLimit) || 0) + delta,
          ),
        ),
        true,
      );
      validation.setFieldTouched('perAccountRedeemLimit', true, false);
    },
    [validation],
  );

  const onChangeStockQuantity = useCallback(
    (e) => {
      const newValue = getSanitizedInteger(e.target.value);
      validation.setFieldValue(
        'stockQuantity',
        Math.max(exchangedQuantity, newValue),
        true,
      );
    },
    [validation, exchangedQuantity],
  );

  const onChangePerAccountLimit = useCallback(
    (e) => {
      validation.setFieldValue(
        'perAccountRedeemLimit',
        getSanitizedInteger(e.target.value),
        true,
      );
    },
    [validation],
  );

  const onCancel = useCallback(() => {
    history.push(ENDPOINT.MANAGER_GIFT_LIST);
  }, [history]);

  const limitStartAtVal = validation.values.limitStartAt || (isVoucherGift
    ? (selectedCampaignDetails?.startAt || gift?.voucherCampaign?.startAt)
    : '');

  const limitEndAtVal = validation.values.limitEndAt || (isVoucherGift
    ? (selectedCampaignDetails?.endAt || gift?.voucherCampaign?.endAt)
    : '');

  const toDatePickerValue = useCallback((value) => {
    if (!value) return undefined;
    return moment(value).isValid() ? dayjs(value) : undefined;
  }, []);

  const disabledStartDate = useCallback((current) => {
    if (!current) return false;
    return current.valueOf() < dayjs().startOf('day').valueOf();
  }, []);

  const disabledEndDate = useCallback(
    (current) => {
      if (!current) return false;
      const startLimit = limitStartAtVal;
      const baseDate = startLimit ? dayjs(startLimit) : dayjs();
      return current.valueOf() < baseDate.startOf('day').valueOf();
    },
    [limitStartAtVal],
  );

  const onChangeLimitStartDatePicker = useCallback(
    (value) => {
      validation.setFieldValue('limitStartAt', value ? value.toDate() : '', true);
      validation.setFieldTouched('limitStartAt', true, false);
    },
    [validation],
  );

  const onChangeLimitEndDatePicker = useCallback(
    (value) => {
      validation.setFieldValue('limitEndAt', value ? value.toDate() : '', true);
      validation.setFieldTouched('limitEndAt', true, false);
    },
    [validation],
  );

  return (
    <div className="page-content">
      <Meta name={PAGE_TITLE} />

      <Loading loading={isLoadingGetGiftDetails} loaderOnPage />

      <Container fluid>
        <Breadcrumb pageTitle={PAGE_TITLE} breadcrumbList={breadcrumbList} />

        <div className={styles.Page}>
          <div className={styles.Wrapper}>
            {/* info gift */}
            <Card className={styles.Section}>
              <div className={styles.SectionTop}>
                <GiftIcon className={styles.Icon} />

                <div className={styles.SectionTopTitle}>
                  <h3>{giftTypeTitle}</h3>
                  <p>
                    Tùy chỉnh khuyến mãi này theo nhu cầu của Quý vị. Chọn ngày,
                    mức giảm giá, đối tượng áp dụng và hơn thế nữa.
                  </p>
                </div>
              </div>
            </Card>
            {/* basic info */}
            <Card className={styles.Section}>
              <h3 className={styles.SectionHeading}>Thông tin cơ bản</h3>

              <div className={styles.FormField}>
                <p className={styles.FormFieldTitle}>Tên quà tặng</p>
                <AppInputValidation
                  inputClassName={styles.FormFieldInput}
                  name="name"
                  keyName="name"
                  placeholder="Nhập tên quà tặng (Ví dụ: eVoucher giảm 15% Grab đưa đón sân bay)"
                  validation={validation}
                  onBlur={validation.handleBlur}
                  limitCharacter={GIFT_NAME_MAX_LENGTH}
                  isShowCounter
                />
              </div>

              <div className={styles.FormField}>
                <p className={styles.FormFieldTitle}>Hình thức vận chuyển</p>
                <div className={styles.OptionGroup}>
                  {DELIVERY_METHOD_OPTIONS.map(({ value, label }) => {
                    const isDisabled = isDeliveryOptionDisabled(value);

                    return (
                      <label
                        key={value}
                        htmlFor={`delivery-${value}`}
                        className={classNames(
                          isDisabled && styles.OptionDisabled,
                        )}
                      >
                        <Input
                          id={`delivery-${value}`}
                          type="radio"
                          name="deliveryMethod"
                          value={value}
                          checked={validation.values.deliveryMethod === value}
                          disabled={isDisabled}
                          onChange={onChangeDeliveryMethod}
                        />
                        <span>{label}</span>
                      </label>
                    );
                  })}
                </div>
                {getFieldError('deliveryMethod') && (
                  <FormFeedback className="d-block">
                    {getFieldError('deliveryMethod')}
                  </FormFeedback>
                )}
              </div>

              <div className={styles.FormField}>
                <p className={styles.FormFieldTitle}>Loại quà</p>
                <div className={styles.OptionGroup}>
                  {GIFT_TYPE_OPTIONS.map(({ value, label }) => (
                    <label
                      key={value}
                      htmlFor={`gift-type-${value}`}
                      className={styles.OptionDisabled}
                    >
                      <Input
                        id={`gift-type-${value}`}
                        type="radio"
                        name="giftType"
                        value={value}
                        checked={validation.values.giftType === value}
                        disabled
                        onChange={onChangeGiftType}
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {isVoucherGift && (
                <div className={styles.FormField}>
                  <div className={styles.FormFieldTitleRow}>
                    <p className={styles.FormFieldTitle}>Chiến dịch voucher</p>
                    {validation.values.voucherCampaign && (
                      <button
                        type="button"
                        className="button-action button-link"
                        onClick={() => history.push(
                          ENDPOINT.MANAGER_GIFT_VOUCHER_CAMPAIGN_DETAIL(
                            validation.values.voucherCampaign,
                          ),
                        )}
                      >
                        Xem chi tiết
                      </button>
                    )}
                  </div>
                  <AppAsyncSelectValidation
                    name="voucherCampaign"
                    placeholder="Chọn chiến dịch voucher"
                    isDisabled
                    onChange={onSelectVoucherCampaign}
                    defaultOptions={voucherCampaignOptions}
                    options={voucherCampaignOptions}
                    value={
                      voucherCampaignOptions.find(
                        (o) => o.value === validation.values.voucherCampaign,
                      ) || null
                    }
                    validation={validation}
                    isUsePureValue
                    isLoading={isLoadingVoucherCampaigns}
                    onSearch={onSearchVoucherCampaign}
                    isHiddenSearchable
                    onMenuClose={onMenuCloseVoucherCampaign}
                  />
                </div>
              )}

              {isVoucherGift && validation.values.voucherCampaign && (
                <div className="w-100">
                  <Row className={styles.ValueGrid}>
                    <Col md={4} className={styles.ValueCol}>
                      <div className={styles.FormField}>
                        <p className={styles.FormFieldTitle}>Ngày bắt đầu</p>
                        <Input
                          type="text"
                          readOnly
                          disabled
                          value={
                            convertIsoDateToString(
                              selectedCampaignDetails?.startAt
                                || gift?.voucherCampaign?.startAt,
                              'DD/MM/YYYY HH:mm',
                            ) || '-'
                          }
                          className={styles.FormFieldInput}
                        />
                      </div>
                    </Col>
                    <Col md={4} className={styles.ValueCol}>
                      <div className={styles.FormField}>
                        <p className={styles.FormFieldTitle}>Ngày kết thúc</p>
                        <Input
                          type="text"
                          readOnly
                          disabled
                          value={
                            convertIsoDateToString(
                              selectedCampaignDetails?.endAt
                                || gift?.voucherCampaign?.endAt,
                              'DD/MM/YYYY HH:mm',
                            ) || '-'
                          }
                          className={styles.FormFieldInput}
                        />
                      </div>
                    </Col>
                    <Col md={4} className={styles.ValueCol}>
                      <div className={styles.FormField}>
                        <p className={styles.FormFieldTitle}>Ngày hết hạn</p>
                        <Input
                          type="text"
                          readOnly
                          disabled
                          value={
                            convertIsoDateToString(
                              selectedCampaignDetails?.voucherExpiryAt
                                || gift?.voucherCampaign?.voucherExpiryAt,
                              'DD/MM/YYYY HH:mm',
                            ) || '-'
                          }
                          className={styles.FormFieldInput}
                        />
                      </div>
                    </Col>
                  </Row>
                </div>
              )}

              <div className={styles.FormField}>
                <p className={styles.FormFieldTitle}>Thuộc tính</p>
                <div className={styles.OptionGroup}>
                  <label htmlFor="gift-is-limited">
                    <Input
                      id="gift-is-limited"
                      type="checkbox"
                      name="isLimited"
                      checked={validation.values.isLimited}
                      onChange={onChangeIsLimited}
                    />
                    <span>Quà giới hạn</span>
                  </label>
                </div>
              </div>

              {isLimitedGift && (
                <div className={styles.FormField}>
                  <p className={styles.FormFieldTitle}>Giới hạn thời gian</p>
                  <Row className={styles.DateRow}>
                    <Col md={5} className={styles.DateCol}>
                      <DatePicker
                        id="limitStartAt"
                        className={styles.CampaignDatePicker}
                        showTime={{ format: 'HH:mm' }}
                        format="DD/MM/YYYY HH:mm"
                        value={toDatePickerValue(limitStartAtVal)}
                        onChange={onChangeLimitStartDatePicker}
                        needConfirm={false}
                        inputReadOnly
                        placeholder="Bắt đầu vào"
                        disabledDate={disabledStartDate}
                      />
                      {getFieldError('limitStartAt') && (
                        <FormFeedback className="d-block mt-2">
                          {getFieldError('limitStartAt')}
                        </FormFeedback>
                      )}
                    </Col>
                    <span className={styles.Dash}>-</span>

                    <Col md={5} className={styles.DateCol}>
                      <DatePicker
                        id="limitEndAt"
                        className={styles.CampaignDatePicker}
                        showTime={{ format: 'HH:mm' }}
                        format="DD/MM/YYYY HH:mm"
                        value={toDatePickerValue(limitEndAtVal)}
                        onChange={onChangeLimitEndDatePicker}
                        needConfirm={false}
                        inputReadOnly
                        placeholder="Kết thúc vào"
                        disabledDate={disabledEndDate}
                        defaultPickerValue={toDatePickerValue(limitStartAtVal)}
                      />
                      {getFieldError('limitEndAt') && (
                        <FormFeedback className="d-block mt-2">
                          {getFieldError('limitEndAt')}
                        </FormFeedback>
                      )}
                    </Col>
                  </Row>
                </div>
              )}

              <div className={styles.FormField}>
                <p className={styles.FormFieldTitle}>Trạng thái</p>
                <div className={styles.OptionGroup}>
                  <label htmlFor="gift-status-hidden">
                    <Input
                      id="gift-status-hidden"
                      type="radio"
                      name="status"
                      value={GIFT_STATUS.HIDDEN}
                      checked={validation.values.status === GIFT_STATUS.HIDDEN}
                      onChange={onChangeStatus}
                    />
                    <span>Ẩn</span>
                  </label>
                  <label htmlFor="gift-status-public">
                    <Input
                      id="gift-status-public"
                      type="radio"
                      name="status"
                      value={GIFT_STATUS.PUBLIC}
                      checked={validation.values.status === GIFT_STATUS.PUBLIC}
                      onChange={onChangeStatus}
                    />
                    <span>Công khai</span>
                  </label>
                </div>
              </div>
            </Card>
            {/* Value & Inventory */}
            <Card className={styles.Section}>
              <h3 className={styles.SectionHeading}>Giá trị &amp; Tồn kho</h3>

              <Row className={styles.ValueGrid}>
                <Col md={6} className={styles.ValueCol}>
                  <div className={styles.FormField}>
                    <p className={styles.FormFieldTitle}>Giá đổi điểm</p>
                    <div className={styles.BcoinField}>
                      <AppInputNumberValidation
                        name="redeemPoints"
                        keyName="redeemPoints"
                        placeholder="0.00"
                        validation={validation}
                        inputClassName={styles.BcoinInput}
                        thousandSeparator=","
                        decimalSeparator="."
                      />
                      <span className={styles.BcoinSuffix}>Xu</span>
                    </div>
                  </div>
                </Col>

                {/* {isVoucherGift && (
                  <Col md={6} className={styles.ValueCol}>
                    <div className={styles.FormField}>
                      <p className={styles.FormFieldTitle}>Hạn sử dụng</p>
                      <div className={styles.BcoinField}>
                        <Input
                          type="text"
                          readOnly
                          disabled
                          value={
                            convertIsoDateToString(
                              selectedCampaignDetails?.voucherExpiryAt ||
                              gift?.voucherCampaign?.voucherExpiryAt,
                              'DD/MM/YYYY HH:mm',
                            ) || '-'
                          }
                          className={styles.BcoinInput}
                        />
                      </div>
                    </div>
                  </Col>
                )} */}
              </Row>

              <Row className={styles.ValueGrid}>
                <Col md={6} className={styles.ValueCol}>
                  <div className={styles.FormField}>
                    <div className={styles.FormFieldTitleRow}>
                      <p className={styles.FormFieldTitle}>Số lượng trong kho</p>
                      {isVoucherGift && validation.values.voucherCampaign && (
                        <button
                          type="button"
                          className="button-action button-link"
                          onClick={() => history.push(
                            ENDPOINT.MANAGER_GIFT_VOUCHER_CAMPAIGN_DETAIL(
                              validation.values.voucherCampaign,
                            ),
                          )}
                        >
                          Xem chi tiết
                        </button>
                      )}
                    </div>
                    <div className={styles.Stepper}>
                      <Button
                        isDefault
                        type="button"
                        classes={classNames(
                          styles.StepperBtn,
                          styles.StepperBtnDecrease,
                        )}
                        onClick={onAdjustStock(-1)}
                        disabled={
                          !validation.values.stockQuantity
                          || validation.values.stockQuantity <= exchangedQuantity
                          || isVoucherGift
                        }
                        aria-label="Giảm số lượng"
                      >
                        <i className="mdi mdi-minus" />
                      </Button>

                      <Input
                        type="number"
                        min={0}
                        max={999}
                        disabled={isVoucherGift}
                        className={styles.StepperInput}
                        value={
                          isVoucherGift
                            ? (selectedCampaignDetails?.totalVoucher ??
                              gift?.voucherCampaign?.totalVoucher ?? 0)
                            : Number(validation.values.stockQuantity)
                        }
                        onChange={onChangeStockQuantity}
                        onKeyDown={onKeyDownIntegerOnly}
                        onFocus={(e) => e.target.select()}
                      />

                      <Button
                        isDefault
                        type="button"
                        classes={classNames(
                          styles.StepperBtn,
                          styles.StepperBtnIncrease,
                        )}
                        onClick={onAdjustStock(1)}
                        disabled={
                          isVoucherGift ||
                          validation.values.stockQuantity >= 999
                        }
                        aria-label="Tăng số lượng"
                      >
                        <PlusIcon className={styles.StepperPlusIcon} />
                      </Button>
                    </div>
                    {getFieldError('stockQuantity') && (
                      <FormFeedback className="d-block">
                        {getFieldError('stockQuantity')}
                      </FormFeedback>
                    )}
                  </div>
                </Col>

                <Col md={6} className={styles.ValueCol}>
                  <div className={styles.FormField}>
                    <p className={styles.FormFieldTitle}>
                      Giới hạn đổi của một tài khoản
                    </p>
                    <div className={styles.Stepper}>
                      <Button
                        isDefault
                        type="button"
                        classes={classNames(
                          styles.StepperBtn,
                          styles.StepperBtnDecrease,
                        )}
                        onClick={onAdjustPerAccountLimit(-1)}
                        disabled={!validation.values.perAccountRedeemLimit}
                        aria-label="Giảm giới hạn"
                      >
                        <i className="mdi mdi-minus" />
                      </Button>
                      <Input
                        type="number"
                        min={0}
                        max={999}
                        className={styles.StepperInput}
                        value={validation.values.perAccountRedeemLimit}
                        onChange={onChangePerAccountLimit}
                        onKeyDown={onKeyDownIntegerOnly}
                        onFocus={(e) => e.target.select()}
                      />
                      <Button
                        isDefault
                        type="button"
                        classes={classNames(
                          styles.StepperBtn,
                          styles.StepperBtnIncrease,
                        )}
                        onClick={onAdjustPerAccountLimit(1)}
                        disabled={
                          validation.values.perAccountRedeemLimit >= 999
                        }
                        aria-label="Tăng giới hạn"
                      >
                        <PlusIcon className={styles.StepperPlusIcon} />
                      </Button>
                    </div>
                    {getFieldError('perAccountRedeemLimit') && (
                      <FormFeedback className="d-block">
                        {getFieldError('perAccountRedeemLimit')}
                      </FormFeedback>
                    )}
                  </div>
                </Col>
              </Row>
            </Card>
            {/* Upload img */}
            <Card className={styles.Section}>
              <h3 className={styles.SectionHeading}>
                Ảnh đại diện
                {getFieldError('images') && (
                  <FormFeedback className="d-block mt-2 fw-normal">
                    {getFieldError('images')}
                  </FormFeedback>
                )}
              </h3>

              <Loading loading={isLoadingUploadMultipleMedia} loaderOnPage />

              {validation.values.images.length > 0 && (
                <div className={styles.ImageButtonWrap}>
                  <Button
                    isDefault
                    classes={styles.ImageButtonCheckAll}
                    onClick={onSelectAllImages}
                  >
                    Chọn tất cả
                  </Button>
                  <Button
                    isDefault
                    classes={styles.ImageButtonUnCheck}
                    onClick={onClearSelectAllImages}
                    disabled={
                      !validation.values.images.some((item) => item.checked)
                    }
                  >
                    Bỏ chọn tất cả
                  </Button>
                  <Button
                    isDefault
                    classes={styles.ImageButtonRemove}
                    onClick={onDeleteSelectedImages}
                    disabled={
                      !validation.values.images.some((item) => item.checked)
                    }
                  >
                    Xóa
                  </Button>
                </div>
              )}

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={onDragImage}
                onDragStart={onDragStart}
                onDragCancel={onDragCancel}
              >
                <SortableContext
                  items={validation.values.images.map((item) => item.id)}
                  strategy={rectSortingStrategy}
                >
                  <div className={styles.ImageList} {...getRootProps()}>
                    <div
                      className={classNames(
                        styles.DropImageLayout,
                        isDragAccept && styles.DropImageActive,
                      )}
                    >
                      <AddPictureIcon width={64} height={64} />
                      <span>THÊM ẢNH</span>
                    </div>

                    <input
                      {...getInputProps()}
                      ref={inputFileRef}
                      onChange={onChangeAddImages}
                    />

                    {validation.values.images.map((image) => (
                      <CardImage
                        key={image.id}
                        item={image}
                        onSelectImage={onSelectImage}
                        style={GIFT_IMAGE_THUMB_STYLE}
                      />
                    ))}

                    <Button
                      isDefault
                      type="button"
                      classes={styles.UploadImage}
                      onClick={onClickOpenFileInput}
                    >
                      <AddPictureIcon width={44} height={44} />
                      <span>THÊM ẢNH</span>
                    </Button>
                  </div>
                </SortableContext>

                <DragOverlay adjustScale style={{ transformOrigin: '0 0' }}>
                  {activeDragImage ? (
                    <GiftImageDragPreview
                      item={activeDragImage}
                      style={GIFT_IMAGE_THUMB_STYLE}
                    />
                  ) : null}
                </DragOverlay>
              </DndContext>
            </Card>

            {/* detail content */}
            <Card className={styles.Section}>
              <h3 className={styles.SectionHeading}>Nội dung chi tiết</h3>

              {GIFT_DETAIL_FIELDS.map(({ name, label, placeholder }) => (
                <div key={name} className={styles.FormField}>
                  <p className={styles.FormFieldTitle}>{label}</p>
                  <AppInputValidation
                    type="textarea"
                    name={name}
                    keyName={name}
                    placeholder={placeholder}
                    validation={validation}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    limitCharacter={GIFT_DETAIL_MAX_LENGTH}
                    isShowCounter
                    isShowError={false}
                    inputClassName={styles.DetailTextarea}
                    style={{ minHeight: 84 }}
                  />
                </div>
              ))}
            </Card>
          </div>
        </div>
      </Container>

      <div className={styles.FormActions}>
        <Button type="button" color={themeColor.SECONDARY} onClick={onCancel}>
          Hủy
        </Button>
        <Button
          type="button"
          color={themeColor.PRIMARY}
          loading={isUpdateGiftLoading}
          disabled={isUpdateGiftLoading}
          onClick={onSaveGift}
        >
          Lưu
        </Button>
      </div>
    </div>
  );
}

ManagerDetail.propTypes = {
  isLoadingUploadMultipleMedia: PropTypes.bool,
  actionUploadMultiMedia: PropTypes.func,
  giftDetails: PropTypes.instanceOf(Object),
  isLoadingGetGiftDetails: PropTypes.bool,
  isUpdateGiftLoading: PropTypes.bool,
  actionGetGiftDetails: PropTypes.func,
  actionUpdateGift: PropTypes.func,
};

ManagerDetail.defaultProps = {
  isLoadingUploadMultipleMedia: false,
  actionUploadMultiMedia: INITIAL_VALUE.FUNCTION,
  giftDetails: null,
  isLoadingGetGiftDetails: false,
  isUpdateGiftLoading: false,
  actionGetGiftDetails: INITIAL_VALUE.FUNCTION,
  actionUpdateGift: INITIAL_VALUE.FUNCTION,
};

export default ManagerDetail;
